import React from "react";
import cx from "clsx";
import { useTranslation } from "react-i18next";
import { prettifyNumbers } from "../../../utils/prettifyNumbers";
import { useCartData } from "../../../providers/cartDataStore";
import { useOrderData } from "../../../providers/orderDataStore";
import { IonIcon } from "@ionic/react";
import Truck from "../../../assets/icon/truck.svg";
import { useConfig } from "../../../providers/config";
import { useMarketMode } from "../../../providers/marketMode";
import { ShippingMethodType } from "@core/types";
import { isFastCourierFeatureEnabled } from "../../../providers/address/taxiMasterShippingUtils";
import { useGetMinimumOrderAmount } from "../../../hooks";
import { useGetDeliveryShippingFeePerKg } from "../../../hooks/useGetDeliveryShippingFeePerKg";

export const CartTotal = () => {
  const config = useConfig();
  const { t } = useTranslation();
  const { mode } = useMarketMode();
  const { getProductsTotalWeight } = useCartData();
  const totalWeight = getProductsTotalWeight();

  const isExpressMaxWeightExceeded = mode === "express" && totalWeight > config.expressMaxWeightKg;

  const feePerKg = useGetDeliveryShippingFeePerKg();
  const { cartSubtotalValue, cartTotalValue } = useCartData();
  const { shippingMethod } = useOrderData();
  const currentShippingCost = shippingMethod?.cost || "0";
  const { isMinimumAmountReached } = useGetMinimumOrderAmount();

  const cartTotalValueWithShippingRounded = prettifyNumbers(cartTotalValue + parseInt(currentShippingCost));
  const cartSubtotalValueWithShippingRounded = prettifyNumbers(cartSubtotalValue + parseInt(currentShippingCost));

  const isCartTotalValueRoundedGreaterThanCartSubtotalValueRounded =
    parseInt(cartSubtotalValueWithShippingRounded) > parseInt(cartTotalValueWithShippingRounded);

  const orderTotalWithShippingRounded = prettifyNumbers(
    isMinimumAmountReached ? cartTotalValue + feePerKg + parseInt(currentShippingCost) : cartTotalValue,
  );

  const isExpressPass = config.isExpressDeliveryEnabled && mode === "express" && !isExpressMaxWeightExceeded;

  const isTaxiMasterTurned = !!(
    isFastCourierFeatureEnabled(config) && shippingMethod?.methodType === ShippingMethodType.taxiMaster
  );

  return (
    <div className="flex flex-col items-center gap-1.5">
      <span className="text-sm font-semibold text-[#939393]">{t("Total cost")}</span>
      <div
        className={cx("flex items-center gap-3", {
          "ml-10": isCartTotalValueRoundedGreaterThanCartSubtotalValueRounded,
        })}
      >
        {/* Стоимость товаров - отображается только если сумма заказа больше суммы товаров */}
        {isCartTotalValueRoundedGreaterThanCartSubtotalValueRounded && (
          <span className="text-[1rem] line-through text-[#3C3C3C]">{cartSubtotalValueWithShippingRounded}</span>
        )}
        {/* Сумма заказа */}
        <span className="text-2xl font-semibold text-[#3C3C3C]">{orderTotalWithShippingRounded} ₽</span>
        {/* Стоимость доставки */}
        {(!!parseInt(currentShippingCost) || feePerKg > 0) && isMinimumAmountReached && (
          <div className="relative flex items-center gap-1 text-primary">
            <span>{"("}</span>
            <span className="text-[1rem]">{Number(currentShippingCost) + feePerKg} ₽</span>
            <IonIcon icon={Truck} />
            <span>{")"}</span>
            {(isExpressPass || isTaxiMasterTurned) && <span className="absolute -top-0.5 -right-6 text-2xl">⚡</span>}
          </div>
        )}
      </div>
      <span className="text-xs text-[#939393] font-bold">
        {t("Total amount will be recalculated by the actual weight of the goods")}
      </span>
    </div>
  );
};
